import type { DomainReadiness, ReadinessBand, SkillEstimate, SkillView, StageId, SubjectId } from '@/core/types';
import { stageLabel } from '@/data/curriculum/structure';
import { catalog } from '@/data/catalog';
import { domainDistance, stageDistance, type EligibilityMap } from './eligibility';
import { demonstratedLevel } from './estimates';
import { B, L, loc, LL } from '@/i18n';

/**
 * DOMAIN READINESS — a per-domain band relative to the curriculum anchor,
 * computed only from assessed skills. Unassessed domains stay UNASSESSED.
 * Domains are never averaged into a single subject score.
 */
export function domainReadiness(
  domainId: string, views: Record<string, SkillView>, estimates: Record<string, SkillEstimate>, elig: EligibilityMap,
): DomainReadiness {
  const domain = catalog.domain(domainId)!;
  const e = elig[domain.subjectId];
  const skills = catalog.skillsInDomain(domainId).filter((s) => {
    const r = views[s.id]?.relevance;
    return r === 'core' || r === 'foundation' || r === 'extension';
  });
  const assessed = skills.filter((s) => (views[s.id]?.evidenceCount ?? 0) > 0);
  const base: DomainReadiness = {
    domainId, band: 'UNASSESSED', provisional: true, assessedSkills: assessed.length, eligibleSkills: skills.length,
    gapSkillIds: [], strongSkillIds: [], relativeLevel: null,
  };
  if (!assessed.length) return base;

  let best = -Infinity;
  let foundationOnly = true;
  let ahead = false;
  let totalEvidence = 0;
  for (const s of assessed) {
    const v = views[s.id];
    const est = estimates[s.id];
    totalEvidence += v.evidenceCount;
    const dist = domainDistance(s, elig); // relative to what this domain expects at the student's stage
    const L = est ? demonstratedLevel(est) : 0;
    if (v.status === 'WEAK' && dist <= 0) base.gapSkillIds.push(s.id);
    if (v.status === 'PROFICIENT' || v.status === 'MASTERED') base.strongSkillIds.push(s.id);
    if (stageDistance(s, e) > 0 && (v.status === 'PROFICIENT' || v.status === 'MASTERED')) ahead = true;
    if (L <= 0.5) continue; // nothing solved — not positive evidence
    let rel = dist + (L - 4.5) / 1.5;
    if (dist < 0 && (v.status === 'PROFICIENT' || v.status === 'MASTERED')) rel = Math.max(rel, -0.5);
    if (dist >= 0) foundationOnly = false;
    best = Math.max(best, rel);
  }

  let band: ReadinessBand;
  const hasGap = base.gapSkillIds.length > 0;
  if (best === -Infinity) band = hasGap ? 'BELOW_EXPECTED' : 'DEVELOPING';
  else if (ahead) band = 'AHEAD';
  else if (best >= 0.75) band = 'ADVANCED';
  else if (best >= -0.5) band = 'INTERMEDIATE';
  else if (best >= -1.5 || !hasGap) band = 'DEVELOPING';
  else band = 'BELOW_EXPECTED';

  if (hasGap && band === 'INTERMEDIATE') band = 'DEVELOPING';

  return {
    ...base,
    band,
    relativeLevel: best === -Infinity ? null : best,
    provisional: totalEvidence < 3 || foundationOnly || assessed.every((s) => views[s.id].status === 'ASSESSED'),
  };
}

export function subjectReadiness(subjectId: SubjectId, views: Record<string, SkillView>, estimates: Record<string, SkillEstimate>, elig: EligibilityMap): DomainReadiness[] {
  return catalog.domainsOf(subjectId).map((d) => domainReadiness(d.id, views, estimates, elig));
}

export const BAND_LABEL: Record<ReadinessBand, string> = loc({
  AHEAD: B('Ahead of grade', 'Foran trinnet'),
  ADVANCED: B('Advanced', 'Avansert'),
  INTERMEDIATE: B('Intermediate', 'På nivå'),
  DEVELOPING: B('Developing', 'Under utvikling'),
  BELOW_EXPECTED: B('Below expected', 'Under forventet'),
  UNASSESSED: B('Unassessed', 'Ikke vurdert'),
});

export function readinessNarrative(subjectId: SubjectId, domains: DomainReadiness[], anchorRaw: string): string {
  const subject = catalog.subject(subjectId)!;
  const anchorLabel = /^(G|VG)\d+$/.test(anchorRaw) ? stageLabel(anchorRaw as StageId).toLowerCase() : anchorRaw;
  const name = (id: string) => catalog.domain(id)?.name ?? id;
  const and = L(' and ', ' og ');
  const assessed = domains.filter((d) => d.band !== 'UNASSESSED');
  const strong = assessed.filter((d) => d.band === 'AHEAD' || d.band === 'ADVANCED');
  const weak = assessed.filter((d) => d.band === 'DEVELOPING' || d.band === 'BELOW_EXPECTED');
  const unassessed = domains.filter((d) => d.band === 'UNASSESSED');
  const parts: string[] = [];
  if (!assessed.length) return LL(`Studio doesn't know your ${subject.name} level yet. Every skill is still UNKNOWN — a short level check gets you started.`, `Studio vet ikke nivået ditt i ${subject.name} ennå. Alle ferdigheter er fortsatt UKJENTE — en kort nivåsjekk får deg i gang.`, { detailed: [`No evidence yet in ${subject.name}. Every skill is still UNKNOWN — a short calibration will start building your profile.`, `Ingen informasjon ennå i ${subject.name}. Alle ferdigheter er fortsatt UKJENTE — en kort kartlegging begynner å bygge profilen din.`] });
  if (strong.length >= Math.ceil(assessed.length / 2)) {
    parts.push(strong.some((d) => d.band === 'AHEAD')
      ? L(`You appear generally ahead of ${anchorLabel} in ${subject.name}`, `Du ser ut til å ligge foran ${anchorLabel} i ${subject.name}`)
      : L(`You appear generally above the expected level in ${subject.name}`, `Du ser ut til å ligge over forventet nivå i ${subject.name}`));
  } else if (weak.length >= Math.ceil(assessed.length / 2)) {
    parts.push(LL(`Some ${subject.name} areas need the basics first, before harder stuff`, `Noen områder i ${subject.name} trenger det grunnleggende først, før vanskeligere stoff`, { detailed: [`Several ${subject.name} areas would benefit from foundation work before harder material`, `Flere områder i ${subject.name} har godt av grunnleggende øving før vanskeligere stoff`] }));
  } else {
    parts.push(LL(`Your ${subject.name} profile is mixed — you're stronger in some areas than others`, `Profilen din i ${subject.name} er variert — du er sterkere i noen områder enn andre`, { detailed: [`Your ${subject.name} profile is mixed, with different strengths in different areas`, `Profilen din i ${subject.name} er variert, med ulike styrker i ulike områder`] }));
  }
  if (weak.length && strong.length) {
    const w = weak.map((d) => name(d.domainId)).join(and);
    parts[0] += weak.length > 1 ? L(`, but ${w} are currently weaker areas`, `, men ${w} er svakere områder nå`) : L(`, but ${w} is currently a weaker area`, `, men ${w} er et svakere område nå`);
  } else if (strong.length && !weak.length && strong.length < assessed.length) {
    parts[0] += L(`, strongest in ${strong.map((d) => name(d.domainId)).join(and)}`, `, sterkest i ${strong.map((d) => name(d.domainId)).join(and)}`);
  }
  let text = `${parts[0]}.`;
  const gapCount = assessed.reduce((s, d) => s + d.gapSkillIds.length, 0);
  if (gapCount) text += ' ' + LL(`Studio found ${gapCount} missing piece${gapCount > 1 ? 's' : ''} to work on — without lowering the rest of the subject.`, `Studio fant ${gapCount} manglende brikke${gapCount > 1 ? 'r' : ''} å jobbe med — uten å senke resten av faget.`, { detailed: [`Studio found ${gapCount} specific prerequisite gap${gapCount > 1 ? 's' : ''} to target — without lowering the rest of the subject.`, `Studio fant ${gapCount} konkrete hull i forkunnskapene å jobbe med — uten å senke resten av faget.`] });
  if (unassessed.length) {
    const u = unassessed.map((d) => name(d.domainId)).join(', ');
    text += ' ' + LL(`${u} ${unassessed.length > 1 ? 'are' : 'is'} not checked yet (unknown, not guessed).`, `${u} er ikke sjekket ennå (ukjent, ikke gjettet).`, { detailed: [`${u} ${unassessed.length > 1 ? 'remain' : 'remains'} unassessed (unknown, not assumed).`, `${u} er ikke vurdert ennå (ukjent, ikke antatt).`] });
  }
  return text;
}
